import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './CheckpointDetailModal.module.scss';
import { IconX, IconAlertCircle, IconCheck, IconClipboard, IconTarget,IconCone2Filled, IconSettings } from '@tabler/icons-react';
import { CheckPointDto, Status } from '../../api/model';
import { usePutCheckpointUpdate } from '../../hooks/usePutCheckpointUpdate';
import { useNotifications } from '../../hooks/useNotifications';
import { useQueryClient } from '@tanstack/react-query';
import RolePicker from '../ui/RolePicker/RolePicker';

interface CheckpointRowData {
  id: string;
  taskId: number;
  checkpointId: number;
  taskName: string;
  taskDescription: string;
  status?: Status;
  validationDate?: string;
  actualValidationDate?: string;
  responsibleRole?: string;
  validationCriteria?: string;
  acceptanceCriteria?: string;
  validationMethod?: string;
  checkpoint?: CheckPointDto;
  task: any;
  deliverObjectId?: number;
  deliverObjectName?: string;
}

interface CheckpointDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  checkpoint: CheckpointRowData | null;
  onSave: (checkpointId: string, updatedData: UpdatedCheckpointData) => void;
}

// interface for compatibility, in future when user approach comes, can be usable
export interface UpdatedCheckpointData {
  comment: string;
  responsiblePerson: string;
  checkDate: string;
  status: Status;
}

const CheckpointDetailModal: React.FC<CheckpointDetailModalProps> = ({
  isOpen,
  onClose,
  checkpoint,
  onSave
}) => {
  const { t } = useTranslation('checkPlan');

  // Form state
  const [validationDate, setValidationDate] = useState('');
  const [status, setStatus] = useState<Status>(Status.PLANNED);
  const [responsibleRole, setResponsibleRole] = useState<string | undefined>(undefined);

  // Hooks for calls and notifications
  const { mutateAsync: updateCheckpoint, isPending } = usePutCheckpointUpdate();
  const { notifySuccess, notifyError } = useNotifications();
  const queryClient = useQueryClient();

  // Initialize form data changes the checkpoint
  useEffect(() => {
    if (checkpoint) {
      const rawActualDate = checkpoint.checkpoint?.actualValidationDate;
      const rawValidationDate = checkpoint.checkpoint?.validationDate;
      const dateToUse = rawActualDate || rawValidationDate;
      setValidationDate(formatDateForInput(dateToUse));
      setStatus(checkpoint.status || Status.PLANNED);
      setResponsibleRole(checkpoint.checkpoint?.responsibleRole || checkpoint.responsibleRole || undefined);
    }
  }, [checkpoint]);

  if (!isOpen || !checkpoint) return null;

  const getStatusIcon = (statusValue: Status) => {
    switch (statusValue) {
      case Status.FINISHED:
        return <IconCheck className={styles.successIcon} />;
      case Status.DELAYED:
        return <IconAlertCircle className={styles.errorIcon} />;
      case Status.PLANNED:
      default:
        return <IconAlertCircle className={styles.warningIcon} />;
    }
  };

  const getStatusText = (statusValue: Status) => {
    switch (statusValue) {
      case Status.FINISHED:
        return t('checkpointDetail.statusOptions.finished');
      case Status.DELAYED:
        return t('checkpointDetail.statusOptions.delayed');
      case Status.PLANNED:
      default:
        return t('checkpointDetail.statusOptions.planned');
    }
  };

  const getStatusColor = (statusValue: Status) => {
    switch (statusValue) {
      case Status.FINISHED:
        return 'success';
      case Status.DELAYED:
        return 'error';
      case Status.PLANNED:
      default:
        return 'warning';
    }
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr || dateStr.trim() === '') return 'N/A';
    try {
      const date = new Date(dateStr);
      // Check if the date is valid
      if (Number.isNaN(date.getTime())) return 'N/A';
      
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit', 
        year: 'numeric'
      });
    } catch {
      return 'N/A';
    }
  };

  const formatDateForInput = (dateStr?: string) => {
    if (!dateStr || dateStr.trim() === '') return '';
    try {
      const date = new Date(dateStr);
      // Check if the date is valid
      if (Number.isNaN(date.getTime())) return '';
      
      return date.toISOString().split('T')[0];
    } catch {
      return '';
    }
  };

  const handleSubmit = async () => {
    if (!checkpoint?.checkpoint?.id) {
      notifyError(t('checkpointDetail.toast.updateTitle'), t('checkpointDetail.toast.idNotFound'));
      return;
    }

    try {
      const updateData: CheckPointDto = {
        ...checkpoint.checkpoint,
        status,
        validationDate: validationDate || undefined,
        responsibleRole: responsibleRole || undefined,
      };

      // api call
      await updateCheckpoint({
        id: checkpoint.checkpoint.id,
        data: updateData
      });

      notifySuccess(t('checkpointDetail.toast.updateTitle'), t('checkpointDetail.toast.updateSuccess'));

      // Refresh data
      queryClient.invalidateQueries({
        predicate: (query) => {
          const key = query.queryKey[0];
          return typeof key === 'string' && key.startsWith('/checkpoint');
        }
      });

      onSave(checkpoint.id, {
        comment: '',
        responsiblePerson: responsibleRole || '',
        checkDate: validationDate,
        status
      });

      // close modal after save
      onClose();
    } catch (error) {
      notifyError(t('checkpointDetail.toast.updateTitle'), error);
    }
  };

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h3 className={styles.modalTitle}>{t('checkpointDetail.title')}</h3>
          <button className={styles.closeButton} onClick={onClose}>
            <IconX />
          </button>
        </div>

        <div className={styles.modalBody}>
          <div className={styles.infoSection}>
            <div className={styles.infoGrid}>
              <div className={styles.infoItem}>
                <div className={styles.infoLabel}>
                  <IconTarget size={16} />
                  {t('checkpointDetail.taskName')}
                </div>
                <div className={styles.infoValue}>{checkpoint.taskName || t('checkpointDetail.notAvailable')}</div>
              </div>
              
              <div className={styles.infoItem}>
                <div className={styles.infoLabel}>
                  <IconClipboard size={16} />
                  {t('checkpointDetail.checkpointId')}
                </div>
                <div className={styles.infoValue}>{checkpoint.checkpointId}</div>
              </div>
              
              <div className={styles.infoItem}>
                <div className={styles.infoLabel}>
                  <IconSettings size={16} />
                  {t('checkpointDetail.validationMethod')}
                </div>
                <div className={styles.infoValue}>{checkpoint.validationMethod || t('checkpointDetail.notAvailable')}</div>
              </div>
              
              <div className={styles.infoItem}>
                <div className={styles.infoLabel}>
                  <IconTarget size={16} />
                  {t('checkpointDetail.acceptanceCriteria')}
                </div>
                <div className={styles.infoValue}>{checkpoint.acceptanceCriteria || t('checkpointDetail.notAvailable')}</div>
              </div>
            </div>
          </div>

          <div className={styles.formGroup}>
            <div className={styles.formLabel}>{t('checkpointDetail.taskDescription')}</div>
            <div className={styles.readOnlyField}>{checkpoint.taskDescription || t('checkpointDetail.noDescription')}</div>
          </div>

          <div className={styles.formGroup}>
            <div className={styles.formLabel}>{t('checkpointDetail.relatedObject')}</div>
            <div className={styles.readOnlyField}>
              {checkpoint.deliverObjectId ? (
                <div className={styles.deliverObjectInfo}>
                  <IconCone2Filled size={16} />
                  <span>ID: {checkpoint.deliverObjectId}</span>
                  {checkpoint.deliverObjectName && (
                    <span> - {checkpoint.deliverObjectName}</span>
                  )}
                </div>
              ) : (
                <span>{t('checkpointDetail.noObject')}</span>
              )}
            </div>
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="responsibleRole" className={styles.formLabel}>{t('checkpointDetail.responsibleRole')}</label>
            <RolePicker
              id="responsibleRole"
              className={styles.formSelect}
              value={responsibleRole}
              onChange={setResponsibleRole}
              placeholder={t('checkpointDetail.notAssigned')}
            />
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="validationDate" className={styles.formLabel}>{t('checkpointDetail.validationDate')}</label>
            <div className={styles.dateInputWrapper}>
              <input
                id="validationDate"
                type="date"
                className={styles.formInput}
                value={validationDate}
                onChange={(e) => setValidationDate(e.target.value)}
              />
            </div>
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="status" className={styles.formLabel}>{t('checkpointDetail.status')}</label>
            <div className={styles.selectWithIcon}>
              <select
                id="status"
                className={styles.formSelect}
                value={status}
                onChange={(e) => setStatus(e.target.value as Status)}
              >
                <option value={Status.PLANNED}>{t('checkpointDetail.statusOptions.planned')}</option>
                <option value={Status.DELAYED}>{t('checkpointDetail.statusOptions.delayed')}</option>
                <option value={Status.FINISHED}>{t('checkpointDetail.statusOptions.finished')}</option>
              </select>
              <div className={styles.statusIcon}>
                {getStatusIcon(status)}
              </div>
            </div>
          </div>

          <div className={styles.qualityCriteriaSection}>
            <h4 className={styles.sectionTitle}>{t('checkpointDetail.validationInfo')}</h4>
            
            <div className={styles.criteriaSection}>
              <div className={styles.criteriaLabel}>
                <span className={styles.labelIcon}><IconAlertCircle /></span>
                {t('checkpointDetail.validationCriteria')}
              </div>
              <div className={styles.criteriaValue}>
                {checkpoint.validationCriteria || t('checkpointDetail.noCriteria')}
              </div>
            </div>
            
            <div className={styles.criteriaSection}>
              <div className={styles.criteriaLabel}>
                <span className={styles.labelIcon}><IconSettings /></span>
                {t('checkpointDetail.currentStatus')}
              </div>
              <div className={styles.criteriaValue}>
                <div className={`${styles.statusBadge} ${styles[getStatusColor(checkpoint.status || Status.PLANNED)]}`}>
                  {getStatusIcon(checkpoint.status || Status.PLANNED)}
                  {getStatusText(checkpoint.status || Status.PLANNED)}
                </div>
              </div>
            </div>
          </div>

          <div className={styles.actionButtons}>
            <button 
              className={styles.cancelButton} 
              onClick={onClose}
            >
              {t('checkpointDetail.cancel')}
            </button>
            <button
              className={styles.saveButton}
              onClick={handleSubmit}
              disabled={isPending}
            >
              {isPending ? t('checkpointDetail.saving') : t('checkpointDetail.save')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckpointDetailModal;